import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ListTodo } from 'lucide-react';
import { SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/stores/authStore';
import { useTaskStore } from '@/stores/taskStore';
import { openTasksFor, tasksPanelHiddenOn, usePageTasks } from './usePageTasks';

/** Pinned to the foot of the sidebar: opens the Tasks panel for the page you're on. */
export function SidebarTasksButton() {
  const canView = usePermission('TASKS');
  const teamActive = useAuthStore((s) => s.user?.active_team?.status === 'active');
  const { pathname } = useLocation();
  const hidden = !canView || !teamActive || tasksPanelHiddenOn(pathname);
  const { board } = usePageTasks(!hidden);
  const panelOpen = useTaskStore((s) => s.panel.open);
  const setPanel = useTaskStore((s) => s.setPanel);
  const { isMobile, setOpenMobile } = useSidebar();

  const count = useMemo(() => {
    if (!board) return 0;
    const done = new Set(board.columns.filter((c) => c.is_done).map((c) => c.id));
    return openTasksFor(board.tasks, done).length;
  }, [board]);

  if (hidden) return null;
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={panelOpen}
          tooltip={count ? `Tasks on this page (${count})` : 'Tasks on this page'}
          aria-label={`Tasks on this page${count ? `: ${count} open` : ''}`}
          onClick={() => {
            setPanel({ open: !panelOpen, taskId: null });
            if (isMobile) setOpenMobile(false);
          }}
        >
          <ListTodo />
          <span>Tasks on this page</span>
        </SidebarMenuButton>
        {count ? (
          <SidebarMenuBadge className="bg-primary text-2xs font-semibold text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground">
            {count}
          </SidebarMenuBadge>
        ) : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
